import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import OwnerSubHeader from "@/components/OwnerSubHeader";
import { Loader2, Plus, Users, UsersRound, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import ComposeModal from "@/components/ComposeModal";

export default function Crews() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/crews");
      const arr = Array.isArray(data) ? data : data?.items || [];
      setItems(arr);
    } catch (ex) { setErr(apiError(ex)); setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete crew "${c.name}"?`)) return;
    try { await api.delete(`/crews/${c.id || c._id}`); reload(); }
    catch (ex) { alert(apiError(ex)); }
  };

  return (
    <div className="space-y-5" data-testid="crews-page">
      <OwnerSubHeader
        title="Crews"
        subtitle="Members · Supervisors · Assignments"
        right={
          <div className="flex gap-2">
            <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => setShow(true)} data-testid="crews-new">
              <Plus className="w-4 h-4" /> New Crew
            </button>
          </div>
        }
      />

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading crews…
        </div>
      ) : items.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <UsersRound className="w-8 h-8 mx-auto mb-2 opacity-60" />
          No crews yet.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => (
            <div key={c.id || c._id} className="panel p-4 flex items-center gap-4" data-testid={`crew-${c.id || c._id}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                   style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                <Users className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base truncate">{c.name || `Crew ${String(c.id).slice(0, 6)}`}</div>
                <div className="text-[11px] text-muted font-mono-tight mt-0.5">
                  {c.supervisor_name || c.supervisor || "No supervisor"} · {(c.members || c.member_ids || []).length} members · {c.site_code || "any site"}
                </div>
              </div>
              <button className="btn btn-sm btn-ghost text-red-400" onClick={() => remove(c)} title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ComposeModal
        open={show}
        onClose={() => setShow(false)}
        onCreated={() => { setShow(false); reload(); }}
        title="Create Crew"
        endpoint="/crews"
        fields={[
          { name: "name", label: "Crew name", required: true, placeholder: "Digger Crew A" },
          { name: "site_code", label: "Site", placeholder: "NP-01" },
          { name: "shift", label: "Shift", type: "select", options: ["morning", "afternoon", "night", "any"] },
          { name: "notes", label: "Notes", type: "textarea", rows: 2 },
        ]}
        testid="crews-create"
      />
    </div>
  );
}
